import { isRejected, isRejectedWithValue, Middleware } from '@reduxjs/toolkit';
import type { ConnectionLossReason } from '@/app/state/connectionLossReason';
import { getSocket, setSocket, subscribeSocket, type SocketState } from '@/app/state/socketState';

// See docs/ai/websocket-events.md → "Transition state model".

const STABLE_MS = 2000;
const NETWORK_STATUSES = new Set<unknown>(['FETCH_ERROR', 500, 502, 503, 504]);
// Backend unreachable through the proxy (gateway errors) re-enters transition; 500 excluded — it can be a genuine app error. See docs/ai/websocket-events.md → "Transition state model".
export const CONNECTION_LOST_STATUSES = new Set<unknown>(['FETCH_ERROR', 502, 503, 504]);

let stableTimer: ReturnType<typeof setTimeout> | null = null;

const clearStableTimer = () => {
  if (stableTimer !== null) {
    clearTimeout(stableTimer);
    stableTimer = null;
  }
};

export const armStableTimer = () => {
  if (!getSocket().inTransition || !getSocket().isConnected) return;
  clearStableTimer();
  stableTimer = setTimeout(() => {
    stableTimer = null;
    // See docs/ai/websocket-events.md → "Transition state model".
    setSocket({ inTransition: false });
  }, STABLE_MS);
};

// Omitted `reason` preserves the current one. See docs/ai/websocket-events.md → "Transition state model".
export const enterTransition = (reason?: ConnectionLossReason | null) => {
  const patch: Partial<SocketState> = { inTransition: true };
  if (reason !== undefined) patch.transitionReason = reason;
  clearStableTimer();
  setSocket(patch);
};

let prev = getSocket();
subscribeSocket(() => {
  const next = getSocket();
  if (!prev.isConnected && next.isConnected && next.inTransition) {
    armStableTimer();
  }
  if (prev.isConnected && !next.isConnected) {
    clearStableTimer();
  }
  prev = next;
});

const transitionMiddleware: Middleware = () => next => action => {
  if (isRejectedWithValue(action) && typeof action.payload === 'object' && action.payload !== null) {
    const status = (action.payload as { status?: unknown }).status;
    const arg = action.meta?.arg as { type?: string } | undefined;
    const isMutation = arg?.type === 'mutation';

    if (CONNECTION_LOST_STATUSES.has(status) && !getSocket().inTransition) {
      enterTransition();
    }

    if (getSocket().inTransition) {
      if (!isMutation) {
        armStableTimer();
        return;
      }
      if (NETWORK_STATUSES.has(status)) {
        armStableTimer();
        return;
      }
    }
  } else if (isRejected(action)) {
    // Aborted/condition-false query rejections still mean the request didn't land. See docs/ai/websocket-events.md → "Suppress".
    const arg = (action as { meta?: { arg?: { type?: string } } }).meta?.arg;
    if (arg?.type === 'query' && getSocket().inTransition) {
      armStableTimer();
      return;
    }
  }

  return next(action);
};

export default transitionMiddleware;
